import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FaSave, FaUndo } from 'react-icons/fa';
import { LuFolderKanban } from 'react-icons/lu';
import { useToast } from '../../../context/ToastContext';
import PageLayout from '../../../components/ui/PageLayout';
import PageHeader from '../../../components/ui/PageHeader';
import SkeletonLoader from '../../../components/ui/SkeletonLoader';
import PortalIconPicker from '../../../components/PortalCMS/PortalIconPicker';

const SLUG = 'portal-documents-page';

const defaultForm = () => ({
  isPublished: true,
  pageTitle: '',
  pageSubtitle: '',
  pageIcon: 'LuFolderKanban',
  mainTabLabel: '',
  mainTabIcon: 'LuUpload',
  chatTabLabel: '',
  chatTabIcon: 'LuMessageSquare',
  searchPlaceholder: '',
  foldersTitle: '',
  emptyTitle: '',
  emptySubtitle: '',
  tableDocumentLabel: '',
  tableDateLabel: '',
  tableActionsLabel: '',
  breadcrumbHomeLabel: '',
  breadcrumbBackLabel: '',
});

const DocumentsPagePortalCMS = () => {
  const navigate = useNavigate();
  const { success: toastSuccess, error: toastError } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [form, setForm] = useState(defaultForm());

  const fetchContent = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/${SLUG}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        setForm({ ...defaultForm(), ...(res.data.data.content || {}), isPublished: res.data.data.isPublished !== false });
      }
    } catch {
      toastError('Failed to load documents page content');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/${SLUG}`,
        { content: form, isPublished: form.isPublished },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) toastSuccess('Documents page saved');
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Restore preloaded documents page defaults?')) return;
    setResetting(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/${SLUG}/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toastSuccess('Defaults restored');
      fetchContent();
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to reset');
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <PageLayout>
        <SkeletonLoader count={4} />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <PageHeader
        title="Documents Page"
        description="Edit the User Portal /documents page labels and text."
        icon={LuFolderKanban}
        actions={
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => navigate('/portal-cms')} className="px-4 py-2 rounded-xl border border-gray-200 dark:border-dark-600 text-sm">Back</button>
            <button type="button" onClick={handleReset} disabled={resetting} className="px-4 py-2 rounded-xl border text-sm flex items-center gap-2">
              <FaUndo className="w-3.5 h-3.5" /> Reset
            </button>
            <button type="button" onClick={handleSave} disabled={saving} className="btn-premium text-sm py-2 px-4 flex items-center gap-2">
              <FaSave className="w-3.5 h-3.5" /> {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        }
      />

      <div className="bg-white dark:bg-dark-800 rounded-2xl border p-6 space-y-4">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.isPublished !== false} onChange={(e) => setForm((prev) => ({ ...prev, isPublished: e.target.checked }))} />
          Published
        </label>

        <h3 className="font-bold pt-2">Page header</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input className="input-modern" value={form.pageTitle} onChange={(e) => setForm((prev) => ({ ...prev, pageTitle: e.target.value }))} placeholder="Page title" />
          <PortalIconPicker value={form.pageIcon} onChange={(icon) => setForm((prev) => ({ ...prev, pageIcon: icon }))} />
        </div>
        <textarea className="input-modern min-h-[70px]" value={form.pageSubtitle} onChange={(e) => setForm((prev) => ({ ...prev, pageSubtitle: e.target.value }))} placeholder="Page subtitle" />

        <h3 className="font-bold pt-2">Tabs</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input className="input-modern" value={form.mainTabLabel} onChange={(e) => setForm((prev) => ({ ...prev, mainTabLabel: e.target.value }))} placeholder="Main documents tab" />
          <PortalIconPicker value={form.mainTabIcon} onChange={(icon) => setForm((prev) => ({ ...prev, mainTabIcon: icon }))} />
          <input className="input-modern" value={form.chatTabLabel} onChange={(e) => setForm((prev) => ({ ...prev, chatTabLabel: e.target.value }))} placeholder="Chat documents tab" />
          <PortalIconPicker value={form.chatTabIcon} onChange={(icon) => setForm((prev) => ({ ...prev, chatTabIcon: icon }))} />
        </div>

        <h3 className="font-bold pt-2">Lists & navigation</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input className="input-modern" value={form.searchPlaceholder} onChange={(e) => setForm((prev) => ({ ...prev, searchPlaceholder: e.target.value }))} placeholder="Search placeholder" />
          <input className="input-modern" value={form.foldersTitle} onChange={(e) => setForm((prev) => ({ ...prev, foldersTitle: e.target.value }))} placeholder="Folders section title" />
          <input className="input-modern" value={form.breadcrumbHomeLabel} onChange={(e) => setForm((prev) => ({ ...prev, breadcrumbHomeLabel: e.target.value }))} placeholder="Breadcrumb home label" />
          <input className="input-modern" value={form.breadcrumbBackLabel} onChange={(e) => setForm((prev) => ({ ...prev, breadcrumbBackLabel: e.target.value }))} placeholder="Back button label" />
        </div>

        <h3 className="font-bold pt-2">Table columns</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input className="input-modern" value={form.tableDocumentLabel} onChange={(e) => setForm((prev) => ({ ...prev, tableDocumentLabel: e.target.value }))} placeholder="Document column" />
          <input className="input-modern" value={form.tableDateLabel} onChange={(e) => setForm((prev) => ({ ...prev, tableDateLabel: e.target.value }))} placeholder="Date column" />
          <input className="input-modern" value={form.tableActionsLabel} onChange={(e) => setForm((prev) => ({ ...prev, tableActionsLabel: e.target.value }))} placeholder="Actions column" />
        </div>

        <h3 className="font-bold pt-2">Empty state</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input className="input-modern" value={form.emptyTitle} onChange={(e) => setForm((prev) => ({ ...prev, emptyTitle: e.target.value }))} placeholder="Empty title" />
          <textarea className="input-modern min-h-[70px]" value={form.emptySubtitle} onChange={(e) => setForm((prev) => ({ ...prev, emptySubtitle: e.target.value }))} placeholder="Empty subtitle" />
        </div>
      </div>
    </PageLayout>
  );
};

export default DocumentsPagePortalCMS;
